import {
  Box,
  Center,
  Paper,
  Skeleton,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'

import type { ErrorEntry } from '@/api/whatsapiStats'
import { useWhatsApiPeriod } from '@/context/WhatsApiPeriodContext'
import { useErrors, isNoApiKeys } from '@/hooks/useWhatsApiStats'
import type { EscopoWhatsApi } from '@/hooks/useEscopoWhatsApi'

const MESSAGE_TRUNCATE_LIMIT = 80

function truncate(value: string, limit = MESSAGE_TRUNCATE_LIMIT): string {
  if (value.length <= limit) return value
  return `${value.slice(0, limit)}…`
}

function formatLastSeen(value: string): string {
  try {
    return new Date(value).toLocaleString('pt-BR')
  } catch {
    return value
  }
}

interface ErrorsTableProps {
  limit?: number
  escopo?: EscopoWhatsApi
}

export function ErrorsTable({ limit = 20, escopo }: ErrorsTableProps = {}) {
  const { period } = useWhatsApiPeriod()
  const query = useErrors(period, limit, { escopo })

  const raw = query.data
  const rows: ErrorEntry[] = raw && !isNoApiKeys(raw) ? (raw.errors ?? []) : []

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Title order={5} mb="sm">
        Erros recentes
      </Title>

      {query.isLoading && <Skeleton height={160} radius="md" />}

      {!query.isLoading && rows.length === 0 && (
        <Center py="lg" data-testid="errors-empty">
          <Text c="dimmed" size="sm">
            Nenhum dado disponível para este período
          </Text>
        </Center>
      )}

      {!query.isLoading && rows.length > 0 && (
        <Box style={{ overflowX: 'auto' }}>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Mensagem</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Ocorrências</Table.Th>
                <Table.Th>Última vez</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row, idx) => {
                const truncated = truncate(row.message)
                const isTruncated = truncated !== row.message
                return (
                  <Table.Tr key={`${row.message}-${idx}`}>
                    <Table.Td>
                      {isTruncated ? (
                        <Tooltip label={row.message} multiline w={320} withArrow>
                          <Text component="span" size="sm">
                            {truncated}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text component="span" size="sm">
                          {row.message}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {row.count.toLocaleString('pt-BR')}
                    </Table.Td>
                    <Table.Td>{formatLastSeen(row.last_seen)}</Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Box>
      )}
    </Paper>
  )
}
